import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/states/ErrorState';
import { DeviceStateBadge } from '@/components/domain/DeviceStateBadge';
import { fetchAdminStats } from '@/api/endpoints/admin';
import { queryKeys, POLL } from '@/api/queryKeys';
import { titleCase } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { AdminStats, DeviceState } from '@/types/api';

const STATE_ORDER: readonly DeviceState[] = ['unclaimed', 'claimed', 'revoked', 'locked', 'rma'];

function Tile({
  label,
  value,
  hint,
  tone = 'neutral',
  to,
}: {
  label: string;
  value: number | string | undefined;
  hint?: string;
  tone?: 'neutral' | 'warn' | 'bad' | 'ok';
  to?: string;
}) {
  const body = (
    <>
      <p className="text-[13px] text-ink-secondary">{label}</p>
      {value === undefined ? (
        <Skeleton className="mt-2 h-8 w-14" />
      ) : (
        <p
          className={cn(
            'mt-1 text-3xl font-semibold tracking-tight',
            tone === 'bad' && 'text-bad',
            tone === 'warn' && 'text-warn',
            tone === 'ok' && 'text-ok',
            tone === 'neutral' && 'text-ink'
          )}
          data-numeric
        >
          {value}
        </p>
      )}
      {hint && <p className="mt-1 text-[12px] text-ink-muted">{hint}</p>}
    </>
  );

  return to ? (
    <Link to={to} className="card block p-4 transition-colors hover:bg-surface-sunken">
      {body}
    </Link>
  ) : (
    <div className="card p-4">{body}</div>
  );
}

/**
 * What a SafeSphere operator opens the console to find out, in the order they ask it:
 * is anything on fire, how many units are in the field and where are they in their
 * lifecycle, and did the manufacturing runs actually reach customers.
 *
 * Note what is deliberately absent: no map, no vehicle list, no "your devices". Those
 * are the customer app's questions. This page never shows an individual's location.
 */
export function OperationsPage() {
  const stats = useQuery({
    queryKey: queryKeys.adminStats,
    queryFn: fetchAdminStats,
    refetchInterval: POLL.summary,
  });

  if (stats.isError) {
    return (
      <>
        <PageHeader title="Operations" />
        <Card>
          <ErrorState error={stats.error} onRetry={stats.refetch} />
        </Card>
      </>
    );
  }

  const d: AdminStats | undefined = stats.data;
  const byState = new Map((d?.devices_by_state ?? []).map((row) => [row.state, row.count]));

  // Ranked loudest-first: an undelivered alert means somebody was not told.
  const attention = [
    d && d.alerts_failed_7d > 0
      ? {
          key: 'alerts',
          tone: 'bad' as const,
          text: `${d.alerts_failed_7d} alert dispatch${d.alerts_failed_7d === 1 ? '' : 'es'} failed in the last 7 days. Somebody was not told.`,
          to: '/alerts',
        }
      : null,
    d && d.devices_silent_24h > 0
      ? {
          key: 'silent',
          tone: 'warn' as const,
          text: `${d.devices_silent_24h} claimed device${d.devices_silent_24h === 1 ? ' has' : 's have'} not reported in over 24 hours. Their owners believe they are covered.`,
          to: '/admin/devices?state=claimed',
        }
      : null,
    d && d.users_without_emergency_contact > 0
      ? {
          key: 'contact',
          tone: 'warn' as const,
          text: `${d.users_without_emergency_contact} owner${d.users_without_emergency_contact === 1 ? ' has' : 's have'} no emergency contact. A critical crash reaches only them.`,
          to: '/admin/customers',
        }
      : null,
  ].filter((x): x is NonNullable<typeof x> => x !== null);

  return (
    <>
      <PageHeader
        title="Operations"
        description="Product health across every customer. Not a fleet view — nothing here is scoped to one account."
      />

      <div className="grid gap-4">
        <Card>
          <CardHeader title="Needs attention" />
          <CardBody>
            {!d ? (
              <Skeleton className="h-12 w-full" />
            ) : attention.length === 0 ? (
              <p className="flex items-center gap-2 text-[13px] text-ok">
                <CheckCircle2 className="size-4" aria-hidden />
                Nothing outstanding. Every dispatch landed and every claimed device is reporting.
              </p>
            ) : (
              <ul className="space-y-2">
                {attention.map((item) => (
                  <li key={item.key}>
                    <Link
                      to={item.to}
                      className={cn(
                        'flex items-start gap-2 rounded-[--radius-control] px-3 py-2.5 text-[13px] transition-opacity hover:opacity-80',
                        item.tone === 'bad' ? 'bg-bad-soft text-bad' : 'bg-warn-soft text-warn'
                      )}
                    >
                      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                      <span className="flex-1">{item.text}</span>
                      <ArrowRight className="mt-0.5 size-4 shrink-0" aria-hidden />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile label="Devices in the field" value={d?.devices_total} to="/admin/devices" />
          <Tile
            label="Never reported"
            value={d?.devices_never_reported}
            hint="Provisioned, never powered on"
            tone={d && d.devices_never_reported > 0 ? 'warn' : 'neutral'}
          />
          <Tile label="Customers" value={d?.users_total} to="/admin/customers" />
          <Tile label="Vehicles" value={d?.vehicles_total} />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Device lifecycle" description="Every unit ever provisioned." />
            <CardBody>
              <ul className="space-y-2">
                {STATE_ORDER.map((state) => (
                  <li key={state} className="flex items-center justify-between gap-3">
                    <Link to={`/admin/devices?state=${state}`} className="hover:opacity-80">
                      <DeviceStateBadge state={state} />
                    </Link>
                    <span className="text-[15px] font-medium text-ink" data-numeric>
                      {d ? (byState.get(state) ?? 0) : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Manufacturing batches"
              description="Units that never reported have not reached a working customer."
            />
            {!d ? (
              <CardBody>
                <Skeleton className="h-24 w-full" />
              </CardBody>
            ) : d.batches.length === 0 ? (
              <CardBody>
                <p className="text-[13px] text-ink-muted">Nothing provisioned yet.</p>
              </CardBody>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="border-b border-line text-left text-ink-muted">
                    <tr>
                      <th className="px-4 py-2 font-medium">Batch</th>
                      <th className="px-4 py-2 text-right font-medium">Units</th>
                      <th className="px-4 py-2 text-right font-medium">Claimed</th>
                      <th className="px-4 py-2 text-right font-medium">Never reported</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {d.batches.map((batch) => (
                      <tr key={batch.hw_batch ?? 'none'}>
                        <td className="px-4 py-2.5">
                          {batch.hw_batch ?? <span className="text-ink-muted">Not recorded</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right" data-numeric>
                          {batch.total}
                        </td>
                        <td className="px-4 py-2.5 text-right" data-numeric>
                          {batch.claimed}
                        </td>
                        <td
                          className={cn(
                            'px-4 py-2.5 text-right',
                            batch.never_reported > 0 && 'font-medium text-warn'
                          )}
                          data-numeric
                        >
                          {batch.never_reported}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Incidents" description="Across every customer." />
            <CardBody>
              <div className="grid grid-cols-2 gap-3">
                <Tile label="Crashes, 7 days" value={d?.crashes_7d} />
                <Tile label="Crashes, 30 days" value={d?.crashes_30d} />
                <Tile
                  label="Failed dispatches, 7 days"
                  value={d?.alerts_failed_7d}
                  tone={d && d.alerts_failed_7d > 0 ? 'bad' : 'ok'}
                />
                <Tile label="Pending dispatches" value={d?.alerts_pending} />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Configuration"
              description="What this deployment is currently doing."
            />
            <CardBody>
              <dl className="space-y-3 text-[13px]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <dt className="font-medium text-ink">Emergency services dispatch</dt>
                    <dd className="text-ink-secondary">
                      Owner and their nominated contact are always alerted. This is the
                      fan-out to responder accounts.
                    </dd>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 rounded-full border px-2 py-0.5 text-[12px] font-medium',
                      d?.dispatch_emergency_services
                        ? 'border-ok/20 bg-ok-soft text-ok'
                        : 'border-line bg-surface-sunken text-ink-secondary'
                    )}
                  >
                    {d === undefined ? '—' : d.dispatch_emergency_services ? 'On' : 'Off'}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <dt className="font-medium text-ink">Severity scoring</dt>
                    <dd className="text-ink-secondary">
                      Impact rules threshold resultant g against speed.
                    </dd>
                  </div>
                  <span className="shrink-0 rounded-full border border-line bg-surface-sunken px-2 py-0.5 text-[12px] font-medium text-ink-secondary">
                    {d === undefined ? '—' : d.scoring_is_rule_based ? 'Impact rules' : 'Model'}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <dt className="font-medium text-ink">Accounts by role</dt>
                  </div>
                  <span className="shrink-0 text-right text-ink-secondary">
                    {(d?.users_by_role ?? []).map((row) => (
                      <span key={row.state} className="block">
                        {titleCase(row.state)} · <span data-numeric>{row.count}</span>
                      </span>
                    ))}
                  </span>
                </div>
              </dl>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}
